import { useFormikContext } from "formik";
import lodashCloneDeep from "lodash/cloneDeep";
import lodashSet from "lodash/set";
import lodashUnset from "lodash/unset";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { apiRequest } from "shared/api/apiRequest";
import { postSocietyProfileWorkflowInstanceIdContract } from "shared/api/contracts/society/societyId/profiles/profileId/workflow/workflowId/instance/workflowInstanceId";
import { getSocietyWorkflowStateContract } from "shared/api/contracts/society/societyId/workflow/workflowId/state";
import { type SocietyWorkflowInstanceBodyInput } from "shared/api/types/society/[societyId]/workflow/[workflowId]/instance";
import AlertTriangleIcon from "shared/components/icons/AlertTriangleIcon";
import { buildDynamicFormValidationSchema } from "shared/components/pillar-form/dynamic-component/buildDynamicFormValidationSchema";
import { PillarFormDynamicInputComponent } from "shared/components/pillar-form/dynamic-component/PillarFormDynamicInputComponent";
import PillarForm from "shared/components/pillar-form/PillarForm";
import { type PillarFormInputComponentConfig } from "shared/components/pillar-form/PillarFormInputComponentConfig";
import PillarModalBody, {
  ModalMaxWidth,
  ModalMinWidth,
} from "shared/components/pillar-modal/NewPillarModalBody";
import { type PillarModalControl } from "shared/components/pillar-modal/usePillarModalControl";
import {
  buildWorkflowStateEventOptions,
  getWorkflowStateEventWarning,
  type WorkflowStateEventOption,
} from "shared/components/workflow-action-modal/workflowStateEventOptions";
import { useCustomSession } from "shared/context/CustomSessionContext";
import { type WorkflowInstanceView } from "shared/mappers/database/workflow/workflow-instance";
import { type WorkflowStateView } from "shared/mappers/database/workflow/workflow-state";
import * as Yup from "yup";

/**
 * Renders the selected state's adminForm elements.
 *
 * The elements are swapped out whenever a different state is picked, so the
 * previous state's values are dropped and the new state's `initialValue`s are
 * seeded here rather than through the form's initialValues. Feeding them into
 * `overrideInitialValues` instead would reinitialize Formik and wipe the
 * already-chosen `setState`/`eventName`.
 */
const AdminSetWorkflowStateFormElements = ({
  elements,
}: {
  elements: PillarFormInputComponentConfig[];
}) => {
  const { setValues } = useFormikContext<Record<string, unknown>>();
  const previousElementNames = useRef<string[]>([]);

  useEffect(() => {
    const elementNames = elements.map((element) => element.name);

    setValues((currentValues) => {
      const nextValues = lodashCloneDeep(currentValues);

      previousElementNames.current.forEach((name) => {
        if (!elementNames.includes(name)) {
          lodashUnset(nextValues, name);
        }
      });

      elements.forEach((element) => {
        lodashSet(nextValues, element.name, element.initialValue);
      });

      return nextValues;
    });

    previousElementNames.current = elementNames;
  }, [elements, setValues]);

  return (
    <>
      {elements.map((element, idx) => (
        <PillarFormDynamicInputComponent
          key={element.name ?? idx}
          element={element}
        />
      ))}
    </>
  );
};

/**
 * Reads the chosen event straight off Formik so it also catches a value typed
 * into the creatable select, which never appears in `eventOptions`.
 */
const AdminSetWorkflowStateEventWarning = ({
  eventOptions,
  selectedState,
  testid,
}: {
  eventOptions: WorkflowStateEventOption[];
  selectedState: WorkflowStateView | null;
  testid: string;
}) => {
  const { values } = useFormikContext<{ eventName?: string }>();

  const warning = getWorkflowStateEventWarning({
    eventName: values.eventName,
    options: eventOptions,
    selectedState,
  });

  if (!warning) {
    return null;
  }

  return (
    <div
      className="mt-0.5 flex w-full items-start gap-1 rounded-md border border-status-warning bg-status-warning-light px-1.5 py-1 text-status-warning-tint"
      data-testid={testid}
    >
      <AlertTriangleIcon className="mt-0.25 h-3 w-3 shrink-0" />
      <p className="text-sm">{warning}</p>
    </div>
  );
};

const AdminSetWorkflowStateModal = ({
  workflowInstance,
  control,
  refetchCallback,
  testid,
}: {
  control: PillarModalControl;
  workflowInstance: WorkflowInstanceView;
  refetchCallback?: () => Promise<void>;
  testid: string;
}) => {
  const { society } = useCustomSession();
  const [selectedState, setSelectedState] = useState<WorkflowStateView | null>(
    null,
  );

  const adminFormElements = useMemo(
    () => selectedState?.adminForm?.formElements ?? [],
    [selectedState],
  );

  const adminSetWorkflowStateFormSchema = useMemo(
    () =>
      buildDynamicFormValidationSchema(adminFormElements, {
        setState: Yup.string().required("Workflow state is required"),
      }),
    [adminFormElements],
  );

  const workflowId = workflowInstance.workflow?.workflowId;

  const eventOptions = useMemo<WorkflowStateEventOption[]>(
    () => buildWorkflowStateEventOptions({ selectedState }),
    [selectedState],
  );

  const handleSubmit = useCallback(
    async (form: Partial<SocietyWorkflowInstanceBodyInput>) => {
      const body: SocietyWorkflowInstanceBodyInput = {
        ...form,
        eventName:
          typeof form.eventName === "string" && form.eventName.trim().length > 0
            ? form.eventName
            : "AdminSetState", // Default event name if not provided
        payloadValue: workflowInstance.workflowInstancePayload?.payloadValue,
        profileId: workflowInstance.profile!.profileId,
        workflowInstancePayloadId: workflowInstance.workflowInstancePayloadId,
        formId: workflowInstance.workflowInstancePayload?.form?.formId,
      };

      await apiRequest({
        contract: postSocietyProfileWorkflowInstanceIdContract,
        inputs: {
          params: {
            societyId: society!.societyId,
            profileId: workflowInstance.profile!.profileId,
            workflowInstanceId: workflowInstance.workflowInstanceId || 0,
            workflowId: workflowInstance.workflow?.workflowId || 0,
          },
          body,
        },
      });

      control.setIsOpen(false);
      if (refetchCallback) {
        await refetchCallback();
      }
    },
    [workflowInstance, control, society, refetchCallback],
  );

  return (
    <PillarModalBody
      control={control}
      title="Update Workflow State"
      maxW={ModalMaxWidth.Xl_1200}
      minW={ModalMinWidth.Lg_900}
      testid={`${testid}-admin-set-workflow-state-modal`}
    >
      <PillarForm
        overrideInitialValues={{
          setState: "",
          eventName: "",
        }}
        handleSubmit={handleSubmit}
        className="flex w-full flex-col items-center"
        yupValidation={adminSetWorkflowStateFormSchema}
      >
        <PillarForm.SelectComponent<
          WorkflowStateView,
          string,
          Record<string, unknown>
        >
          additionalClasses="w-full"
          name="setState"
          label="Workflow State"
          placeholder="Select a state..."
          queryCallback={async ({ text }) =>
            await apiRequest({
              contract: getSocietyWorkflowStateContract,
              inputs: {
                params: {
                  societyId: society!.societyId,
                  workflowId: workflowId!,
                },
              },
              filters: text ? { name: String(text) } : undefined,
            })
          }
          labelCallback={(s) =>
            `${s.name} (${s.description}) - ${s.type.toUpperCase()}`
          }
          valueCallback={(s) => s.name}
          onChangeCallback={(newValue, _actionMeta, results, formikContext) => {
            const selectedValue =
              newValue && "value" in newValue ? newValue.value : undefined;
            setSelectedState(
              results.find((r) => r.name === selectedValue) ?? null,
            );
            // The event select remounts on the new state and renders blank, but
            // its Formik value survives - clear it so the previous state's
            // event cannot be submitted against this one.
            formikContext.setFieldValue("eventName", "");
          }}
          testid={`${testid}-admin-set-workflow-state-modal-state-select`}
        />

        {selectedState ? (
          <>
            <PillarForm.SelectComponent<
              WorkflowStateEventOption,
              string,
              Record<string, unknown>
            >
              additionalClasses="w-full"
              key={selectedState.name}
              name="eventName"
              label="Start Event"
              // handleSubmit substitutes AdminSetState when this is left blank,
              // so the placeholder names it rather than leaving the admin to
              // find out from the state history afterwards.
              placeholder="Defaults to AdminSetState"
              tooltip="The event that is triggering the workflow at this state."
              allowCreate
              queryCallback={async ({ text }) => {
                const lowerText = String(text ?? "").toLowerCase();
                const results = eventOptions.filter((option) =>
                  option.label.toLowerCase().includes(lowerText),
                );

                return {
                  results,
                  totalResults: results.length,
                  page: 1,
                  pageSize: results.length,
                };
              }}
              labelCallback={(option) => option.label}
              valueCallback={(option) => option.event}
              testid={`${testid}-admin-set-workflow-state-modal-event-select`}
            />

            <AdminSetWorkflowStateEventWarning
              eventOptions={eventOptions}
              selectedState={selectedState}
              testid={`${testid}-admin-set-workflow-state-modal-event-warning`}
            />
          </>
        ) : null}

        <AdminSetWorkflowStateFormElements elements={adminFormElements} />

        <PillarForm.LoadingSubmitButton
          label="Save"
          className="mt-2"
          testid={`${testid}-admin-set-workflow-state-modal-submit`}
        />
      </PillarForm>
    </PillarModalBody>
  );
};

export default AdminSetWorkflowStateModal;
